import { Dispatch, useCallback, useEffect, useMemo, useState } from "react";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { EntityConstants, INamedEntity } from "../../../api/GenericTypes";
import { Button, ButtonProps } from "../../../common/buttons/Button/Button";
import { IconNames, LucideIcon } from "../../../common/icon/LucideIcon";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { Modal } from "../../../ELN/common/ELNModal/ELNModal";
import { FormHeader } from "../../../common/forms/FormHeader";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { FormFieldLayout } from "../../../common/formfields/FormFieldLayouts";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { zIndex } from "../../../api/CommonConstants";
import {
  PdfOutputTypeFormOptions,
  PdfPageSeparatorFormOptions,
  PdfSettings,
  useExportPDF,
} from "../../../common/tables/ExportPdfButton/useExportPdf";
import Select from "react-select";
import { fileExportJobsConstants } from "../../../api/FileExportJobs";

export interface GenericEntityExportButtonProps extends Omit<ButtonProps, "children"> {
  btnClass?: string;
  hideLabel?: boolean;
  onClickCallback?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
  defaultPdfSettings?: PdfSettings;
  icon?: IconNames;
  label?: string;
  skipPdfSettingsModal?: boolean;
}

export const GenericEntityExportButton = <
  Entity extends GenericEntity & Partial<INamedEntity>,
  ComponentProps extends GenericEntityExportButtonProps
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];

  const defaultPdfSettings: PdfSettings = useMemo(() => {
    var fileName = !!componentProps?.defaultPdfSettings?.fileName?.length
      ? componentProps.defaultPdfSettings.fileName
      : entity?.name ?? entityConstants.entitySingular;
    return {
      outputType: "Combined",
      separator: "None",
      ...componentProps?.defaultPdfSettings,
      fileName: fileName.trim().replaceAll(/[^a-zA-Z0-9_-]/g, "_"),
    };
  }, [componentProps?.defaultPdfSettings, entity?.name, entityConstants.entitySingular]);

  const [pdfSettings, setPdfSettings] = useState<PdfSettings>(componentProps?.defaultPdfSettings ?? defaultPdfSettings);

  useEffect(() => {
    !showModal && setPdfSettings(defaultPdfSettings);
  }, [defaultPdfSettings, showModal]);

  const { exportToPdf, loading } = useExportPDF({
    entityConstants,
    filters: { ids: !!entity?.id ? [entity.id] : [] },
    pdfSettings,
  });
  const handleExport = useCallback(async () => {
    if (!entity?.id) return;
    exportToPdf();
    setShowModal(false);
  }, [exportToPdf, entity?.id]);

  const { canRead } = useEntityPermissions({ entityTypeId });

  if (!entity || !entityConstants) return null;
  return (
    <>
      <Button
        onClick={(e) => {
          setShowModal(true);
          componentProps?.onClickCallback?.(e);
        }}
        loading={loading}
        disabled={!canRead(entity)}
        {...componentProps}
        className={componentProps?.btnClass ?? "btn btn-block btn-sm btn-ghost-secondary flex align-center gap-5"}
      >
        <LucideIcon name={componentProps?.icon ?? fileExportJobsConstants.icon} />
        {!componentProps?.hideLabel && (componentProps?.label ?? "Export to PDF")}
      </Button>

      {showModal && !componentProps?.skipPdfSettingsModal && (
        <PdfExportSettingsModal
          showModal={showModal}
          setShowModal={setShowModal}
          pdfSettings={pdfSettings}
          setPdfSettings={setPdfSettings}
          defaultPdfSettings={defaultPdfSettings}
          handleExport={handleExport}
          entityConstants={entityConstants}
        />
      )}
    </>
  );
};

export const PdfExportSettingsModal = ({
  showModal,
  setShowModal,
  pdfSettings,
  setPdfSettings,
  defaultPdfSettings,
  handleExport,
  entityConstants,
  isMultipleEntities = false,
}: {
  showModal: boolean;
  setShowModal: Dispatch<React.SetStateAction<boolean>>;
  pdfSettings: PdfSettings;
  setPdfSettings: Dispatch<React.SetStateAction<PdfSettings>>;
  defaultPdfSettings: PdfSettings;
  handleExport: () => void;
  entityConstants: EntityConstants;
  isMultipleEntities?: boolean;
}) => {
  const [customizeFileName, setCustomizeFileName] = useState<boolean>(false);
  const [customizeOutputType, setCustomizeOutputType] = useState<boolean>(false);
  const [customizePageSeparator, setCustomizePageSeparator] = useState<boolean>(false);

  useEffect(() => {
    if (customizeFileName) {
      setPdfSettings((prevState) => ({ ...prevState, fileName: "" }));
    } else {
      setPdfSettings((prevState) => ({ ...prevState, fileName: defaultPdfSettings.fileName }));
    }
  }, [customizeFileName, defaultPdfSettings.fileName, setPdfSettings]);
  useEffect(() => {
    if (!customizeOutputType) {
      setPdfSettings((prevState) => ({ ...prevState, outputType: defaultPdfSettings.outputType }));
    }
  }, [customizeOutputType, defaultPdfSettings.outputType, setPdfSettings]);
  useEffect(() => {
    if (!customizePageSeparator) {
      setPdfSettings((prevState) => ({ ...prevState, separator: defaultPdfSettings.separator }));
    }
  }, [customizePageSeparator, defaultPdfSettings.separator, setPdfSettings]);
  return (
    <Modal
      isOpen={showModal}
      onClose={() => {
        setPdfSettings(defaultPdfSettings);
        setShowModal(false);
      }}
    >
      <Modal.Body>
        <div style={{ minWidth: "50vw", padding: "10px 20px" }}>
          <>
            <FormHeader title="Export to PDF" subtitle={`${toUppercase(entityConstants.entitySingular)}`} />

            <fieldset>
              <legend style={{ padding: "0 10px" }}>PDF settings</legend>
            </fieldset>

            <FormFieldLayout id="" label="File name" horizontal>
              <div className="flex align-center gap-5">
                <input
                  id="fileName"
                  value={pdfSettings.fileName}
                  className="form form-control"
                  onChange={(e) => {
                    const { value } = e.target;
                    setPdfSettings((prevState) => ({ ...prevState, fileName: value }));
                  }}
                  readOnly={!customizeFileName}
                />
                <button
                  className="btn btn-default flex align-center gap-5"
                  style={{ minWidth: "130px", justifyContent: "center" }}
                  onClick={() => {
                    setCustomizeFileName((prevState) => !prevState);
                  }}
                >
                  <LucideIcon name={customizeFileName ? "x" : "settings"} />
                  {customizeFileName ? "Reset" : "Customize"}
                </button>
                <div style={{ width: "50px", textAlign: "center" }}>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Popover id="popover-info" style={{ textAlign: "justify", zIndex: zIndex.zIndexPortalDropdown }}>
                        The file name which will be used for the exported PDF.
                      </Popover>
                    }
                  >
                    <LucideIcon name="info" color={"var(--gray-400)"} />
                  </OverlayTrigger>
                </div>
              </div>
            </FormFieldLayout>

            {!!isMultipleEntities && (
              <>
                <FormFieldLayout id="" label="Output type" horizontal>
                  <div className="flex align-center gap-5">
                    <div style={{ width: "100%" }}>
                      <Select
                        id="outputType"
                        isSearchable={false}
                        value={PdfOutputTypeFormOptions.find((option) => option.value === pdfSettings.outputType)}
                        options={PdfOutputTypeFormOptions}
                        placeholder="Select output type..."
                        menuPosition="fixed"
                        onChange={(e) => {
                          e?.value && setPdfSettings((prevState) => ({ ...prevState, outputType: e.value }));
                        }}
                        isDisabled={!customizeOutputType}
                      />
                    </div>
                    <button
                      className="btn btn-default flex align-center gap-5"
                      style={{ minWidth: "130px", justifyContent: "center" }}
                      onClick={() => {
                        setCustomizeOutputType((prevState) => !prevState);
                      }}
                    >
                      <LucideIcon name={customizeOutputType ? "x" : "settings"} />
                      {customizeOutputType ? "Reset" : "Customize"}
                    </button>
                    <div style={{ width: "50px", textAlign: "center" }}>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Popover
                            id="popover-info"
                            style={{ textAlign: "justify", zIndex: zIndex.zIndexPortalDropdown }}
                          >
                            {`The output type of the PDF. If you select "${PdfOutputTypeFormOptions[0].label}", all ${entityConstants.entityPlural} will be exported to a single PDF file. If you select "${PdfOutputTypeFormOptions[1].label}", each ${entityConstants.entitySingular} will be exported to a separate PDF file.`}
                          </Popover>
                        }
                      >
                        <LucideIcon name="info" color={"var(--gray-400)"} />
                      </OverlayTrigger>
                    </div>
                  </div>
                </FormFieldLayout>

                {pdfSettings.outputType === "Combined" && (
                  <FormFieldLayout id="" label="Separator" horizontal>
                    <div className="flex align-center gap-5">
                      <div style={{ width: "100%" }}>
                        <Select
                          id="pageSeparator"
                          isSearchable={false}
                          value={PdfPageSeparatorFormOptions.find((option) => option.value === pdfSettings.separator)}
                          options={PdfPageSeparatorFormOptions}
                          placeholder="Select separator..."
                          menuPosition="fixed"
                          onChange={(e) => {
                            e?.value && setPdfSettings((prevState) => ({ ...prevState, separator: e.value }));
                          }}
                          isDisabled={!customizePageSeparator}
                        />
                      </div>
                      <button
                        className="btn btn-default flex align-center gap-5"
                        style={{ minWidth: "130px", justifyContent: "center" }}
                        onClick={() => {
                          setCustomizePageSeparator((prevState) => !prevState);
                        }}
                      >
                        <LucideIcon name={customizePageSeparator ? "x" : "settings"} />
                        {customizePageSeparator ? "Reset" : "Customize"}
                      </button>
                      <div style={{ width: "50px", textAlign: "center" }}>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Popover
                              id="popover-info"
                              style={{ textAlign: "justify", zIndex: zIndex.zIndexPortalDropdown }}
                            >
                              {`The separator which will be used between the ${entityConstants.entityPlural}. If you select "${PdfPageSeparatorFormOptions[0].label}", no separator will be added. If you select "${PdfPageSeparatorFormOptions[1].label}", a larger margin will be added between the ${entityConstants.entityPlural}. If you select "${PdfPageSeparatorFormOptions[2].label}", a page break will be added between the ${entityConstants.entityPlural}. If you select "${PdfPageSeparatorFormOptions[3].label}", a horizontal line will be added between the pages.`}
                            </Popover>
                          }
                        >
                          <LucideIcon name="info" color={"var(--gray-400)"} />
                        </OverlayTrigger>
                      </div>
                    </div>
                  </FormFieldLayout>
                )}
              </>
            )}
          </>
        </div>
      </Modal.Body>
      <Modal.Controls>
        <div className="flex align-center gap-5" style={{ justifyContent: "flex-end", marginTop: "5px" }}>
          <button
            className="btn btn-default"
            onClick={() => {
              setShowModal(false);
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              handleExport();
              setShowModal(false);
            }}
          >
            {`Export ${entityConstants.entitySingular}`}
          </button>
        </div>
      </Modal.Controls>
    </Modal>
  );
};
